import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { getEventCoordinates } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GavelIcon from '@mui/icons-material/Gavel';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ReplayIcon from '@mui/icons-material/Replay';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SyncIcon from '@mui/icons-material/Sync';
import TagIcon from '@mui/icons-material/Tag';
import HillChart from './HillChart';
import MemberAvatars from './MemberAvatars';
import { matchAssignee } from './AssigneePicker';
import { colorFor, initialsFor } from './memberRoles';
import ScopeBoard from './ScopeBoard';
import ScopeDrawer from './ScopeDrawer';
import BetScratchpad from './BetScratchpad';
import BetOpenPRs from './BetOpenPRs';
import CommentThread from './CommentThread';
import {
  getBetTodos,
  moveScopeTodo,
  getBetTasks,
  addBetTask,
  updateBetTask,
  deleteBetTask,
  setBetTaskOrder,
  addScopeTodo,
  returnTodoToScratchpad,
  assignScope,
} from '../../utils/shapeUpApi';
import {
  prepareImage,
  uploadTaskAttachment,
  deleteAttachment,
  describeUploadError,
  pastedImageName,
  MAX_IMAGE_BYTES,
} from '../../utils/attachmentsApi';
import { timeAgo, isStale } from '../../utils/relativeTime';

// Keep the dragged pill centred on the pointer instead of letting a long title
// drift the DragOverlay preview away from where the cursor actually is.
const snapCenterToCursor = ({ activatorEvent, draggingNodeRect, transform }) => {
  if (draggingNodeRect && activatorEvent) {
    const coords = getEventCoordinates(activatorEvent);
    if (!coords) return transform;
    return {
      ...transform,
      x: transform.x + coords.x - draggingNodeRect.left - draggingNodeRect.width / 2,
      y: transform.y + coords.y - draggingNodeRect.top - draggingNodeRect.height / 2,
    };
  }
  return transform;
};

// A manual scope has no Jira key, so its todos are keyed by the scope's own id.
// The server keys the two families the same way, so they share one map without
// collision.
const todoKeyFor = (scope) => scope.jiraKey || scope.id;

// A stable empty-trails reference so the default prop doesn't hand HillChart a
// fresh object every render, which would defeat its memoization.
const EMPTY_TRAILS = {};

const APPETITE_LABEL = {
  small: 'Small batch',
  big: 'Big batch',
};

const OUTCOME_LABEL = {
  shipped: 'Shipped',
  circuit_breaker: 'Circuit breaker',
  abandoned: 'Abandoned',
};

const OUTCOME_COLOR = {
  shipped: 'success',
  circuit_breaker: 'warning',
  abandoned: 'default',
};

const OUTCOME_HELP = {
  shipped: 'Finished and in production.',
  circuit_breaker: 'Time ran out. It stops here unless it wins a new bet.',
  abandoned: 'Called off.',
};

/**
 * How long since anyone placed a dot on this hill, phrased so the answer "never"
 * reads as the finding it is rather than as missing data.
 */
const judgmentLabel = (judgment) => {
  if (judgment.judged === 0) return 'Nobody has placed a dot here';
  if (judgment.daysSinceJudged === 0) return 'Dots placed today';
  if (judgment.daysSinceJudged === 1) return 'Dots last placed yesterday';
  return `Dots last placed ${judgment.daysSinceJudged} days ago`;
};

const judgmentHint = (judgment) => {
  const parts = [];
  if (judgment.unjudged > 0) {
    parts.push(`${judgment.unjudged} of ${judgment.open} open scopes sit wherever their Jira status puts them.`);
  }
  if (judgment.provisional > 0) {
    parts.push(`${judgment.provisional} came from a status the hill map does not cover.`);
  }
  parts.push('Only a person can say whether the unknowns are resolved, so a hill nobody touches reports the workflow instead.');
  return parts.join(' ');
};

/**
 * The Jira freshness line for a bet, told honestly.
 *
 * A successful read is reported by its age. A failing or switched-off sync says
 * so instead, because "read 3 hours ago" beside data that is actually stuck is
 * the exact reassurance we are trying not to give: the number would keep looking
 * fine while the board silently diverged from Jira. The detail rides along in a
 * tooltip so the line stays short.
 *
 * @returns {{ text: string, warn: boolean, tooltip: string|null }}
 */
const describeSync = (bet) => {
  const readText = bet.lastSyncedAt ? `Jira read ${timeAgo(bet.lastSyncedAt)}` : 'Never synced with Jira';

  if (bet.lastSyncOutcome === 'failed') {
    return {
      text: `Jira sync failing (last read ${bet.lastSyncedAt ? timeAgo(bet.lastSyncedAt) : 'never'})`,
      warn: true,
      tooltip: bet.lastSyncError || 'The last sync attempt did not complete.',
    };
  }

  if (bet.lastSyncOutcome === 'skipped') {
    return {
      text: 'Not syncing automatically',
      warn: true,
      tooltip: bet.lastSyncError || 'The background sync could not run for this workspace.',
    };
  }

  // Succeeded, or has never run: fall back to plain age, warned only once the
  // data itself is old enough to distrust.
  return { text: readText, warn: isStale(bet.lastSyncedAt), tooltip: null };
};

/**
 * One bet: its appetite, the hill of its scopes, and the parked ones below.
 */
const BetCard = ({
  bet,
  onSync,
  onMoveScope,
  onClearOverride,
  onTogglePark,
  onExcludeScope,
  onRenameScope,
  onHammerScope,
  onRestateScope,
  onChangeStatus,
  onChangeAssignee,
  onScopeChanged,
  onCommentsRead,
  definitionOfDone = [],
  dodStandard = '',
  onAddScope,
  onCreateScope,
  onEditBet,
  onDeleteBet,
  onRebet,
  onCreateSlackChannel,
  syncing = false,
  canEdit = true,
  jiraEnabled = true,
  // False when the deployment has no object storage configured, in which case
  // the attach controls are hidden rather than offered and made to fail.
  attachmentsEnabled = false,
  statusOptions = [],
  linkToDetail = true,
  showScopeBoard = false,
  trails = EMPTY_TRAILS,
  showTrails = false,
  isFavorite = false,
  onToggleFavorite,
  dragHandleProps,
  draggable = false,
  sx,
  hillMaxWidth,
}) => {
  // The open scope lives in the URL (?scope=<id>) so a drawer view is
  // shareable and the back button closes it. Scope ids are globally unique, so
  // on the multi-bet overview only the bet that owns the scope opens its drawer.
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeParam = searchParams.get('scope');
  const selectedScopeId = bet.scopes.some(scope => scope.id === scopeParam) ? scopeParam : null;
  const setSelectedScopeId = useCallback((id) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (id) next.set('scope', id);
      else next.delete('scope');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Elements/regions that own their own click. A click starting inside any of
  // these should not also navigate to the bet.
  const CARD_NAV_IGNORE =
    'a, button, input, textarea, select, [role="button"], svg, .MuiChip-root, [data-no-card-nav]';

  const handleCardNavigate = (event) => {
    if (!linkToDetail || event.defaultPrevented) return;
    if (event.target.closest(CARD_NAV_IGNORE)) return;
    navigate(`/shape-up/bets/${bet.id}`);
  };

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [todosByScope, setTodosByScope] = useState(null);
  const [loadingTodos, setLoadingTodos] = useState(false);
  const [todosError, setTodosError] = useState(null);
  const [movingKey, setMovingKey] = useState(null);
  const todosRequested = useRef(false);

  // The bet's scratchpad, seeded from the hill payload so it renders without a
  // fetch. Re-seeds whenever a fresh bet payload arrives (e.g. after a reload),
  // which replaces optimistic local edits with the server's truth.
  const [tasks, setTasks] = useState(bet.tasks || []);
  const [activeTask, setActiveTask] = useState(null);
  const [taskError, setTaskError] = useState(null);
  // Which task is mid-upload, which image is mid-delete, and the last upload
  // failure. Kept apart from taskError so a rejected image explains itself in
  // place instead of as a toast about the task.
  const [uploadingTaskId, setUploadingTaskId] = useState(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const [attachmentError, setAttachmentError] = useState(null);
  useEffect(() => { setTasks(bet.tasks || []); }, [bet.tasks]);

  // 5px activation so a click on the checkbox, inline edit, or delete is not
  // read as the start of a drag. Independent of ShapeUpPage's bet-reorder
  // context; nesting is fine because each drag starts from its own handle.
  const taskSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Hammered scopes leave the hill: the decision was not to do them, so showing
  // them as work in flight would misread the chart.
  // Memoized on bet.scopes so a scope selection elsewhere doesn't hand HillChart
  // a freshly-filtered array and defeat its memoization.
  const live = useMemo(() => bet.scopes.filter(scope => !scope.isHammered), [bet.scopes]);

  // Count of PRs that need the current user's review across all scopes
  const reviewCount = useMemo(() => {
    let count = 0;
    for (const scope of bet.scopes || []) {
      for (const pr of scope.pullRequests || []) {
        if (pr.needsYourReview) count++;
      }
    }
    // Also count any bet-level PRs that need review (if they're included in openPullRequests)
    for (const pr of bet.openPullRequests || []) {
      if (pr.needsYourReview) count++;
    }
    return count;
  }, [bet.scopes, bet.openPullRequests]);
  const hammered = useMemo(() => bet.scopes.filter(scope => scope.isHammered), [bet.scopes]);
  const onHill = useMemo(() => live.filter(scope => !scope.isParked), [live]);
  const parked = useMemo(() => live.filter(scope => scope.isParked), [live]);
  const selectedScope = bet.scopes.find(scope => scope.id === selectedScopeId) || null;

  // Who owns what on this bet, tallied client-side so a reviewer sees the split
  // without opening every scope. Scope counts are always available; todo counts
  // fill in once the board's todos have loaded. Members with nothing owned are
  // left out so the line stays about the people actually carrying work.
  const ownership = useMemo(() => {
    const members = bet.members || [];
    if (!members.length) return [];
    const counts = new Map(members.map(m => [m.userId, { member: m, scopes: 0, todos: 0 }]));
    (bet.scopes || []).forEach(scope => {
      const owner = matchAssignee(scope, members);
      if (owner && counts.has(owner.userId)) counts.get(owner.userId).scopes += 1;
    });
    Object.values(todosByScope || {}).forEach(list => {
      (list || []).forEach(todo => {
        const owner = matchAssignee(todo, members);
        if (owner && counts.has(owner.userId)) counts.get(owner.userId).todos += 1;
      });
    });
    return [...counts.values()].filter(c => c.scopes > 0 || c.todos > 0);
  }, [bet.members, bet.scopes, todosByScope]);

  // Stable handlers for the hill so the memoized HillChart isn't re-rendered by
  // fresh inline closures when an unrelated bet's selection changes the URL.
  const handleSelectScopeFromHill = useCallback(
    (scope) => setSelectedScopeId(scope.id),
    [setSelectedScopeId],
  );
  const handlePlaceholderClick = useCallback(() => {
    if (!canEdit) return;
    if (jiraEnabled) onAddScope(bet);
    else onCreateScope?.(bet);
  }, [canEdit, jiraEnabled, onAddScope, onCreateScope, bet]);

  const closeMenu = () => setMenuAnchor(null);

  // Todos back both the card badges and the drawer, so the bet owns one copy.
  const loadTodos = useCallback(async () => {
    if (!bet.id) return;
    setLoadingTodos(true);
    try {
      const data = await getBetTodos(bet.id);
      setTodosByScope(data.todosByScope || {});
      setTodosError(null);
    } catch (err) {
      setTodosError(err.response?.data?.message || (jiraEnabled ? 'Could not load todos from Jira.' : 'Could not load todos.'));
    } finally {
      setLoadingTodos(false);
    }
  }, [bet.id, jiraEnabled]);

  // Lazy: a multi-bet overview would otherwise fire a Jira read per card up
  // front, so the fetch waits until the board is shown or a scope is opened,
  // then runs once.
  const needTodos = showScopeBoard || Boolean(selectedScopeId);
  useEffect(() => {
    if (needTodos && !todosRequested.current) {
      todosRequested.current = true;
      loadTodos();
    }
  }, [needTodos, loadTodos]);

  const handleTodosChanged = useCallback((key, todos) => {
    setTodosByScope(prev => ({ ...(prev || {}), [key]: todos }));
  }, []);

  /**
   * Moving a todo reparents its Jira subtask. The lists change immediately
   * because the move should feel done, and a refusal from Jira reloads rather
   * than trying to reconstruct what was true before.
   */
  const moveTodo = useCallback(async (from, todo, targetScopeId) => {
    const target = live.find(scope => scope.id === targetScopeId);
    if (!target || !from || target.id === from.id) return;

    const fromKey = todoKeyFor(from);
    const targetKey = todoKeyFor(target);

    setMovingKey(todo.key);
    setTodosError(null);
    setTodosByScope(prev => ({
      ...(prev || {}),
      [fromKey]: (prev?.[fromKey] || []).filter(item => item.key !== todo.key),
      [targetKey]: [...(prev?.[targetKey] || []), todo],
    }));

    try {
      const moved = await moveScopeTodo(from.id, todo.key, target.id);
      setTodosByScope(prev => ({
        ...(prev || {}),
        [targetKey]: (prev?.[targetKey] || []).map(item => (item.key === todo.key ? moved : item)),
      }));
    } catch (err) {
      const label = todo.summary || todo.key;
      setTodosError(err.response?.data?.message || (jiraEnabled ? `Could not move ${todo.key} in Jira.` : `Could not move ${label}.`));
      await loadTodos();
    } finally {
      setMovingKey(null);
    }
  }, [live, jiraEnabled, loadTodos]);

  /**
   * Send a todo back to the scratchpad. In Jira mode this deletes the subtask;
   * in manual mode the local ScopeTodo. Either way the server creates a new
   * scratchpad task from the todo's summary, which is appended to the bet's
   * tasks. The todo leaves the drawer at once; a refusal reloads the todos.
   */
  const returnTodo = useCallback(async (scope, todo) => {
    if (!scope) return;
    const key = todoKeyFor(scope);
    setMovingKey(todo.key);
    setTodosError(null);
    setTodosByScope(prev => ({
      ...(prev || {}),
      [key]: (prev?.[key] || []).filter(item => item.key !== todo.key),
    }));

    try {
      const created = await returnTodoToScratchpad(scope.id, todo.key);
      setTasks(prev => [...prev, created]);
    } catch (err) {
      const label = todo.summary || todo.key;
      setTodosError(err.response?.data?.message || (jiraEnabled ? `Could not return ${todo.key} to the scratchpad.` : `Could not return ${label} to the scratchpad.`));
      await loadTodos();
    } finally {
      setMovingKey(null);
    }
  }, [jiraEnabled, loadTodos]);

  // Other live scopes this scope's todos can be handed to, as a menu (the drawer
  // shows one scope, so there is no drag target).
  const moveTargetsFor = (scope) => live
    .filter(other => other.id !== scope?.id)
    .map(other => ({
      id: other.id,
      label: other.jiraKey
        ? `${other.jiraKey} · ${other.jiraSummary || 'Untitled'}`
        : (other.jiraSummary || 'Untitled'),
    }));

  // The server is the source of truth for a task's id and order, so a quiet
  // refetch reconciles the scratchpad after a partial failure without guessing.
  const reloadTasks = useCallback(async () => {
    try {
      const data = await getBetTasks(bet.id);
      setTasks(data.tasks || []);
    } catch {
      // Leave the current list; the next full reload will correct it.
    }
  }, [bet.id]);

  /**
   * Upload images onto a task that already exists, one at a time.
   *
   * Sequential rather than parallel: each is downscaled on a canvas first, and
   * running several of those at once on a large screenshot is what makes the tab
   * stutter. Each success lands in the list on its own, so a later failure does
   * not discard the ones that worked.
   */
  const uploadToTask = useCallback(async (taskId, files) => {
    setAttachmentError(null);
    setUploadingTaskId(taskId);

    let lastError = null;
    for (const file of files) {
      try {
        // A pasted screenshot arrives as a nameless blob, so give it one before
        // it becomes an attachment filename and, later, a Jira attachment.
        const named = file.name
          ? file
          : new File([file], pastedImageName(file.type), { type: file.type });

        const { file: prepared, width, height } = await prepareImage(named);

        if (prepared.size > MAX_IMAGE_BYTES) {
          lastError = 'That image is too large, even after resizing.';
          continue;
        }

        const created = await uploadTaskAttachment(taskId, prepared, { width, height });
        setTasks(prev => prev.map(t => (
          t.id === taskId
            ? { ...t, attachments: [...(t.attachments || []), created] }
            : t
        )));
      } catch (error) {
        lastError = describeUploadError(error);
      }
    }

    setUploadingTaskId(null);
    if (lastError) setAttachmentError(lastError);
  }, []);

  /**
   * @param {string} title
   * @param {File[]} [pendingImages] - images picked before the task existed,
   *   attached as soon as it does
   */
  const handleAddTask = useCallback(async (title, pendingImages = []) => {
    let created;
    try {
      created = await addBetTask(bet.id, title);
      setTasks(prev => [...prev, created]);
    } catch {
      setTaskError('Could not add the task.');
      return;
    }
    if (pendingImages.length) {
      await uploadToTask(created.id, pendingImages);
    }
  }, [bet.id, uploadToTask]);

  const handleAttachToTask = useCallback((task, files) => {
    uploadToTask(task.id, files);
  }, [uploadToTask]);

  const handleDeleteAttachment = useCallback(async (task, attachment) => {
    setAttachmentError(null);
    setDeletingAttachmentId(attachment.id);
    try {
      await deleteAttachment(attachment.id);
      setTasks(prev => prev.map(t => (
        t.id === task.id
          ? { ...t, attachments: (t.attachments || []).filter(a => a.id !== attachment.id) }
          : t
      )));
    } catch (error) {
      setAttachmentError(describeUploadError(error));
    } finally {
      setDeletingAttachmentId(null);
    }
  }, []);

  const handleToggleTask = useCallback(async (task, done) => {
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, done } : t)));
    try {
      await updateBetTask(task.id, { done });
    } catch {
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, done: task.done } : t)));
      setTaskError('Could not update the task.');
    }
  }, []);

  const handleEditTask = useCallback(async (task, title) => {
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, title } : t)));
    try {
      await updateBetTask(task.id, { title });
    } catch {
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, title: task.title } : t)));
      setTaskError('Could not rename the task.');
    }
  }, []);

  const handleDeleteTask = useCallback(async (task) => {
    let restore;
    setTasks(prev => { restore = prev; return prev.filter(t => t.id !== task.id); });
    try {
      await deleteBetTask(task.id);
    } catch {
      setTasks(restore);
      setTaskError('Could not delete the task.');
    }
  }, []);

  const handleReorderTasks = useCallback(async (nextTasks) => {
    let restore;
    setTasks(prev => { restore = prev; return nextTasks; });
    try {
      await setBetTaskOrder(bet.id, nextTasks.map(t => t.id));
    } catch {
      setTasks(restore);
      setTaskError('Could not save the new order.');
    }
  }, [bet.id]);

  /**
   * Graduate a task into a scope's real todos. It reuses the todo endpoint, so a
   * ScopeTodo is written in manual mode and a Jira subtask in Jira mode, then the
   * task is deleted. The task leaves the scratchpad at once; a failed create puts
   * it back, and a failed delete after a successful create leaves the todo and
   * quietly reconciles the scratchpad.
   *
   * The task id goes along so the server can move the task's images with it: to
   * Jira as attachments on the new subtask, or onto the manual todo. That has to
   * happen before the delete below, since deleting the task takes its images too.
   */
  const assignTaskToScope = useCallback(async (taskId, scopeId) => {
    const task = tasks.find(t => t.id === taskId);
    const target = live.find(s => s.id === scopeId);
    if (!task || !target) return;

    let restore;
    setTasks(prev => { restore = prev; return prev.filter(t => t.id !== taskId); });

    let result;
    try {
      result = await addScopeTodo(scopeId, task.title, taskId);
    } catch {
      setTasks(restore);
      setTaskError(jiraEnabled ? 'Could not create the Jira subtask.' : 'Could not assign the task.');
      return;
    }

    // The todo was created but an image did not follow it. Worth saying, since
    // the person can see the image is no longer where they left it.
    if (result?.attachmentWarning) {
      setTaskError(result.attachmentWarning);
    }

    try {
      await deleteBetTask(taskId);
    } catch {
      // The todo exists but the task lingered; refetch so the two agree.
      await reloadTasks();
    }
    // Refresh the scope's 0/N badge so the graduated todo is counted.
    loadTodos();
  }, [tasks, live, jiraEnabled, reloadTasks, loadTodos]);

  /**
   * Assign a bet member to the scope itself. Persists to Jira for a Jira-backed
   * scope and locally for a manual one; the server returns the reconciled scope,
   * which carries the display name Jira actually recorded. Optimistic so the
   * avatar updates on click, reverting to the previous owner if the write fails.
   */
  const handleAssignScopeMember = useCallback(async (scope, userId) => {
    if (!scope) return;
    const member = (bet.members || []).find(m => m.userId === userId) || null;
    const previous = { assigneeUserId: scope.assigneeUserId ?? null, assigneeName: scope.assigneeName ?? null };

    onScopeChanged?.({ id: scope.id, assigneeUserId: userId || null, assigneeName: member?.name || null });
    try {
      const updated = await assignScope(scope.id, userId || null);
      onScopeChanged?.(updated);
    } catch (err) {
      onScopeChanged?.({ id: scope.id, ...previous });
      setTaskError(err.response?.data?.message || 'Could not assign the scope.');
    }
  }, [bet.members, onScopeChanged]);

  const handleTaskDragStart = useCallback((event) => {
    setActiveTask(tasks.find(t => t.id === event.active.id) || null);
  }, [tasks]);

  const handleTaskDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveTask(null);
    if (!over) return;

    const overId = String(over.id);
    if (overId.startsWith('scope-')) {
      assignTaskToScope(active.id, overId.slice('scope-'.length));
      return;
    }
    if (active.id !== over.id) {
      const oldIndex = tasks.findIndex(t => t.id === active.id);
      const newIndex = tasks.findIndex(t => t.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        handleReorderTasks(arrayMove(tasks, oldIndex, newIndex));
      }
    }
  }, [tasks, assignTaskToScope, handleReorderTasks]);

  return (
    <Card sx={{ mb: 3, ...sx }}>
      <CardContent
        onClick={handleCardNavigate}
        sx={linkToDetail ? {
          cursor: 'pointer',
          transition: 'background-color 120ms ease',
          '&:hover': { bgcolor: 'action.hover' },
        } : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          {draggable && (
            <Tooltip title="Drag to reorder (only you see this order)">
              <IconButton
                size="small"
                aria-label="Drag to reorder bet"
                sx={{ cursor: 'grab', touchAction: 'none', mt: 0.25 }}
                {...(dragHandleProps || {})}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              {linkToDetail ? (
                <Link
                  component={RouterLink}
                  to={`/shape-up/bets/${bet.id}`}
                  variant="h6"
                  underline="hover"
                  color="inherit"
                  sx={{ fontWeight: 700 }}
                >
                  {bet.name}
                </Link>
              ) : (
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{bet.name}</Typography>
              )}
              <Chip size="small" variant="outlined" label={APPETITE_LABEL[bet.appetite] || bet.appetite} />
              {/* A bet that has been here before is the strongest hint that the
                  problem was shaping rather than execution. */}
              {(bet.attempt || 1) > 1 && (
                <Tooltip title="This work has been bet on before. Worth asking whether it was shaped well enough.">
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    icon={<ReplayIcon sx={{ fontSize: 14 }} />}
                    label={`Attempt ${bet.attempt}`}
                  />
                </Tooltip>
              )}
              {bet.outcome && (
                <Tooltip title={bet.outcomeNote || OUTCOME_HELP[bet.outcome] || ''}>
                  <Chip
                    size="small"
                    color={OUTCOME_COLOR[bet.outcome]}
                    variant={bet.outcome === 'shipped' ? 'filled' : 'outlined'}
                    label={OUTCOME_LABEL[bet.outcome]}
                  />
                </Tooltip>
              )}
              {reviewCount > 0 && (
                <Tooltip title={`${reviewCount} PR${reviewCount === 1 ? '' : 's'} awaiting your review`}>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    icon={<RateReviewIcon sx={{ fontSize: 14 }} />}
                    label={`${reviewCount} to review`}
                  />
                </Tooltip>
              )}
              {bet.jiraEpicKey && (
                <Link
                  href={bet.jiraUrl || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}
                >
                  {bet.jiraEpicKey}
                  <OpenInNewIcon sx={{ fontSize: 13 }} />
                </Link>
              )}
              {bet.pitchUrl && (
                <Link
                  href={bet.pitchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 14 }} />
                  Pitch
                </Link>
              )}
            </Box>
            {bet.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {bet.description}
              </Typography>
            )}
            {/* Faces are the detail page's job. On the dashboard grid a row of
                avatars per card (times many cards) reads as noise, so the team
                strip and the ownership summary below are gated on showScopeBoard. */}
            {((showScopeBoard && bet.members?.length > 0) || bet.slackChannelName || bet.tags?.length > 0
              || bet.interruptions) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                {showScopeBoard && <MemberAvatars members={bet.members} showEmpty={false} />}
                {/* Shown on every card, not gated on showScopeBoard like the
                    avatars are: time this bet lost to an emergency is the single
                    most useful thing to know when reading why it went the way it
                    did, and it is exactly what gets forgotten by the retro. */}
                {bet.interruptions && (
                  <Tooltip
                    title={
                      `Interrupted by: ${bet.interruptions.titles.slice(0, 3).join('; ')}`
                      + (bet.interruptions.titles.length > 3 ? ` and ${bet.interruptions.titles.length - 3} more` : '')
                    }
                  >
                    <Chip
                      size="small"
                      color="error"
                      variant="outlined"
                      icon={<LocalFireDepartmentIcon sx={{ fontSize: 14 }} />}
                      label={
                        bet.interruptions.costDays
                          ? `${bet.interruptions.costDays}d lost to ${bet.interruptions.count} emergenc${bet.interruptions.count === 1 ? 'y' : 'ies'}`
                          : `${bet.interruptions.count} emergenc${bet.interruptions.count === 1 ? 'y' : 'ies'}`
                      }
                      component={RouterLink}
                      to="/bugs"
                      clickable
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  </Tooltip>
                )}
                {(bet.tags || []).map(tag => (
                  <Chip
                    key={tag.slug}
                    label={tag.label}
                    size="small"
                    variant="outlined"
                    component={RouterLink}
                    to={`/shape-up/tags/${encodeURIComponent(tag.slug)}`}
                    clickable
                    sx={{ height: 20, fontSize: 11 }}
                  />
                ))}
                {bet.slackChannelName && (
                  <Link
                    // app_redirect resolves in whichever workspace the reader is
                    // signed in to, so no workspace domain needs storing.
                    href={`https://slack.com/app_redirect?channel=${bet.slackChannelId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}
                  >
                    <TagIcon sx={{ fontSize: 13 }} />
                    {bet.slackChannelName}
                  </Link>
                )}
              </Box>
            )}
            {showScopeBoard && ownership.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '.08em', color: 'text.secondary' }}>
                  OWNS
                </Typography>
                {ownership.map(({ member, scopes, todos }) => {
                  const parts = [];
                  if (scopes) parts.push(`${scopes} scope${scopes === 1 ? '' : 's'}`);
                  if (todos) parts.push(`${todos} todo${todos === 1 ? '' : 's'}`);
                  return (
                    <Tooltip key={member.userId} title={`${member.name || 'Unknown'} · ${parts.join(', ')}`}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: colorFor(member.userId || member.name) }}>{initialsFor(member.name)}</Avatar>
                        <Typography variant="caption" color="text.secondary">{parts.join(' · ')}</Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            )}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {onToggleFavorite && (
            <Tooltip title={isFavorite ? 'Unstar (only you see this)' : 'Star as a favorite (only you see this)'}>
              <IconButton
                size="small"
                aria-label={isFavorite ? 'Unstar bet' : 'Star bet'}
                onClick={() => onToggleFavorite(bet)}
                color={isFavorite ? 'warning' : 'default'}
              >
                {isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}

          {canEdit && bet.jiraEpicKey && (
            <Tooltip title={`Pull the latest child issues of ${bet.jiraEpicKey}`}>
              <span>
                <Button
                  size="small"
                  startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
                  onClick={() => onSync(bet)}
                  disabled={syncing}
                >
                  Sync
                </Button>
              </span>
            </Tooltip>
          )}

          {canEdit && (
            <>
              <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="Bet actions">
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                {onEditBet && (
                  <MenuItem onClick={() => { closeMenu(); onEditBet(bet); }}>
                    <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Edit bet
                  </MenuItem>
                )}
                {!jiraEnabled && onCreateScope && (
                  <MenuItem onClick={() => { closeMenu(); onCreateScope(bet); }}>
                    <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add scope
                  </MenuItem>
                )}
                {jiraEnabled && onCreateScope && (
                  <MenuItem onClick={() => { closeMenu(); onCreateScope(bet); }} disabled={!bet.jiraEpicKey}>
                    <NoteAddOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> New scope, created in Jira
                  </MenuItem>
                )}
                {jiraEnabled && (
                  <MenuItem onClick={() => { closeMenu(); onAddScope(bet); }}>
                    <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add a scope by Jira key
                  </MenuItem>
                )}
                {/* Only where there is no channel yet, which covers both a bet
                    created without one and one whose creation failed. */}
                {onCreateSlackChannel && !bet.slackChannelId && (
                  <MenuItem onClick={() => { closeMenu(); onCreateSlackChannel(bet); }}>
                    <TagIcon fontSize="small" sx={{ mr: 1 }} /> Create a Slack channel
                  </MenuItem>
                )}
                {onRebet && (
                  <MenuItem onClick={() => { closeMenu(); onRebet(bet); }}>
                    <ReplayIcon fontSize="small" sx={{ mr: 1 }} /> Bet on this again
                  </MenuItem>
                )}
                {onDeleteBet && (
                  <MenuItem onClick={() => { closeMenu(); onDeleteBet(bet); }}>
                    <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete bet
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
            {bet.stats.done} done
          </Box>
          {` · ${bet.stats.inProgress} in progress · ${bet.stats.notStarted} not started`}
          {bet.stats.parked > 0 && ` · ${bet.stats.parked} parked`}
          {bet.stats.hammered > 0 && ` · ${bet.stats.hammered} hammered`}
          {bet.stats.dodMet > 0 && ` · ${bet.stats.dodMet} met the definition of done`}
          {bet.stats.stuck > 0 && (
            <Box component="span" sx={{ color: 'warning.main', fontWeight: 700 }}>
              {` · ${bet.stats.stuck} not moving`}
            </Box>
          )}
        </Typography>

        {showScopeBoard && bet.openPullRequests?.length > 0 && (
          <BetOpenPRs
            pullRequests={bet.openPullRequests}
            onSelectScope={(scopeId) => setSelectedScopeId(scopeId)}
          />
        )}

        {/* Two different kinds of freshness, and the second one matters more.
            The first says how old the data is; the second says whether anyone is
            still making calls about where the work sits, or whether the chart has
            quietly become a redrawing of the Jira board. */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
          {bet.jiraEpicKey && (() => {
            const sync = describeSync(bet);
            const line = (
              <Typography
                variant="caption"
                sx={{
                  color: sync.warn ? 'warning.main' : 'text.disabled',
                  cursor: sync.tooltip ? 'help' : 'default',
                }}
              >
                {sync.text}
              </Typography>
            );
            return sync.tooltip ? <Tooltip title={sync.tooltip}>{line}</Tooltip> : line;
          })()}
          {bet.judgment?.open > 0 && (
            <Tooltip title={judgmentHint(bet.judgment)}>
              <Typography
                variant="caption"
                sx={{ color: bet.judgment.isStale ? 'warning.main' : 'text.disabled', cursor: 'help' }}
              >
                {judgmentLabel(bet.judgment)}
              </Typography>
            </Tooltip>
          )}
        </Stack>

        <Box data-no-card-nav>
          <HillChart
            scopes={onHill}
            selectedScopeId={selectedScopeId}
            onSelectScope={handleSelectScopeFromHill}
            onMoveScope={onMoveScope}
            onPlaceholderClick={canEdit ? handlePlaceholderClick : undefined}
            readOnly={!canEdit}
            trails={trails}
            showTrails={showTrails}
            maxWidth={hillMaxWidth}
          />
        </Box>

        {onHill.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
            {!jiraEnabled
              ? 'Nothing on the hill yet. Add a scope to start shaping.'
              : bet.jiraEpicKey
                ? `Nothing on the hill yet. Sync to pull the child issues of ${bet.jiraEpicKey}.`
                : 'Nothing on the hill yet. Link this bet to a Jira epic, or add a scope by key.'}
          </Typography>
        )}

        {/* One DndContext wraps the scratchpad and the scope board so a task can
            be dragged onto a scope to graduate into its todos. Independent of
            ShapeUpPage's outer bet-reorder context. The scratchpad shows even
            with nothing on the hill; drop targets appear once a scope is up. */}
        {showScopeBoard && (
          <DndContext
            sensors={taskSensors}
            collisionDetection={pointerWithin}
            onDragStart={handleTaskDragStart}
            onDragEnd={handleTaskDragEnd}
            onDragCancel={() => setActiveTask(null)}
          >
            <BetScratchpad
              tasks={tasks}
              canEdit={canEdit}
              onAdd={handleAddTask}
              onToggleDone={handleToggleTask}
              onEditTitle={handleEditTask}
              onDelete={handleDeleteTask}
              onAttach={attachmentsEnabled ? handleAttachToTask : undefined}
              onDeleteAttachment={handleDeleteAttachment}
              uploadingTaskId={uploadingTaskId}
              deletingAttachmentId={deletingAttachmentId}
              attachmentError={attachmentError}
            />

            {onHill.length > 0 && (
              <ScopeBoard
                scopes={onHill}
                selectedScopeId={selectedScopeId}
                onSelectScope={(scope) => setSelectedScopeId(scope?.id || null)}
                todosByScope={todosByScope}
                loadingTodos={loadingTodos}
                error={todosError}
                onReload={loadTodos}
                onRenameScope={canEdit ? onRenameScope : undefined}
                manual={!jiraEnabled}
                assignEnabled={canEdit}
                members={bet.members || []}
              />
            )}

            <DragOverlay modifiers={[snapCenterToCursor]}>
              {activeTask ? (
                <Paper
                  variant="outlined"
                  sx={{ px: 1.5, py: 0.75, borderRadius: 1, bgcolor: 'background.paper', boxShadow: 3, maxWidth: 260 }}
                >
                  <Typography variant="body2" noWrap>{activeTask.title}</Typography>
                </Paper>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}

        {/* One drawer for every entry point: a hill dot or a scope card opens it,
            on the detail page and the cycle overview alike. */}
        <ScopeDrawer
          scope={selectedScope}
          open={Boolean(selectedScope)}
          onClose={() => setSelectedScopeId(null)}
          onClearOverride={onClearOverride}
          onTogglePark={onTogglePark}
          onExclude={(scope) => { setSelectedScopeId(null); onExcludeScope(scope); }}
          onRename={onRenameScope}
          onHammer={onHammerScope ? (scope) => { setSelectedScopeId(null); onHammerScope(scope); } : undefined}
          onRestate={onRestateScope}
          onChangeStatus={onChangeStatus}
          onChangeAssignee={onChangeAssignee}
          onChangeAssigneeMember={handleAssignScopeMember}
          onScopeChanged={onScopeChanged}
          onCommentsRead={onCommentsRead}
          members={bet.members || []}
          manual={!jiraEnabled}
          readOnly={!canEdit}
          attachmentsEnabled={attachmentsEnabled}
          statusOptions={statusOptions}
          definitionOfDone={definitionOfDone}
          dodStandard={dodStandard}
          preloadedTodos={selectedScope ? (todosByScope?.[todoKeyFor(selectedScope)] ?? null) : null}
          moveTargets={selectedScope ? moveTargetsFor(selectedScope) : []}
          onTodosChanged={(next) => selectedScope && handleTodosChanged(todoKeyFor(selectedScope), next)}
          onMoveTodo={(todo, targetScopeId) => moveTodo(selectedScope, todo, targetScopeId)}
          onReturnToScratchpad={(todo) => returnTodo(selectedScope, todo)}
          movingKey={movingKey}
        />

        {hammered.length > 0 && (
          <>
            <Divider sx={{ mt: 2, mb: 1.5, borderStyle: 'dashed' }} />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, letterSpacing: '.08em', color: 'warning.main' }}
              >
                HAMMERED
              </Typography>
              {hammered.map(scope => (
                <Chip
                  key={scope.id}
                  size="small"
                  variant="outlined"
                  color="warning"
                  icon={<GavelIcon sx={{ fontSize: 14 }} />}
                  label={scope.jiraSummary || scope.jiraKey}
                  onClick={canEdit && onHammerScope ? () => onHammerScope(scope) : undefined}
                  title={scope.hammeredReason || (canEdit ? 'Put this scope back in the bet' : undefined)}
                />
              ))}
            </Stack>
          </>
        )}

        {parked.length > 0 && (
          <>
            <Divider sx={{ mt: 2, mb: 1.5, borderStyle: 'dashed' }} />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, letterSpacing: '.08em', color: 'text.secondary' }}
              >
                PARKED
              </Typography>
              {parked.map(scope => (
                <Chip
                  key={scope.id}
                  size="small"
                  variant="outlined"
                  label={scope.jiraSummary || scope.jiraKey}
                  onClick={canEdit ? () => onTogglePark(scope) : undefined}
                  title={canEdit ? 'Unpark this scope' : undefined}
                />
              ))}
            </Stack>
          </>
        )}

        <Divider sx={{ mt: 2, mb: 0.5 }} />
        <Box data-no-card-nav>
          <CommentThread commentableType="bet" commentableId={bet.id} />
        </Box>

        <Snackbar
          open={Boolean(taskError)}
          autoHideDuration={5000}
          onClose={() => setTaskError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" variant="filled" onClose={() => setTaskError(null)}>
            {taskError}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default BetCard;
